import { getApiBaseUrl } from "../config/runtimeConfig";
import { ApiError } from "../obras/obrasApi";

const apiBaseUrl = getApiBaseUrl();

export { ApiError };

export interface WorkDiaryAuthorDto {
  id?: string | null;
  name: string;
}

export interface WorkDiaryAttachmentDto {
  id: number;
  fileName: string;
  contentType: string;
  sizeInBytes: number;
}

export interface WorkDiaryAttachmentUploadDto {
  id?: number;
  fileName: string;
  contentType: string;
  sizeInBytes: number;
  url?: string | null;
}

export interface WorkDiaryEntryDto {
  id: number;
  workId: number;
  entryDate: string;
  content: string;
  author: WorkDiaryAuthorDto;
  attachments: WorkDiaryAttachmentDto[];
  created: string;
  lastModified: string;
}

export interface WorkDiaryEntriesVm {
  entries?: WorkDiaryEntryDto[];
}

export interface CreateWorkDiaryEntryPayload {
  workId: number;
  entryDate: string;
  content: string;
  attachments?: WorkDiaryAttachmentUploadDto[];
}

export interface UpdateWorkDiaryEntryPayload {
  entryDate: string;
  content: string;
  attachments?: WorkDiaryAttachmentUploadDto[];
}

async function request<T>(path: string, options?: RequestInit): Promise<T> {
  const response = await fetch(`${apiBaseUrl}${path}`, {
    ...options,
    credentials: "include",
  });

  if (!response.ok) {
    const text = await response.text();
    throw new ApiError(response.status, text || response.statusText);
  }

  const text = await response.text();
  if (!text) {
    return undefined as T;
  }

  return JSON.parse(text) as T;
}

export function getWorkDiaryEntries(
  workId: number,
): Promise<WorkDiaryEntriesVm> {
  return request<WorkDiaryEntriesVm>(`/api/workdiary?workId=${workId}`);
}

export function uploadWorkDiaryAttachment(
  file: File,
): Promise<WorkDiaryAttachmentUploadDto> {
  const formData = new FormData();
  formData.append("attachment", file, file.name || "anexo");

  return request<WorkDiaryAttachmentUploadDto>("/api/workdiary/attachments", {
    method: "POST",
    body: formData,
  });
}

export function createWorkDiaryEntry(
  payload: CreateWorkDiaryEntryPayload,
): Promise<WorkDiaryEntryDto> {
  return request<WorkDiaryEntryDto>("/api/workdiary", {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
    },
    body: JSON.stringify(payload),
  });
}

export function updateWorkDiaryEntry(
  id: number,
  payload: UpdateWorkDiaryEntryPayload,
): Promise<WorkDiaryEntryDto> {
  return request<WorkDiaryEntryDto>(`/api/workdiary/${id}`, {
    method: "PUT",
    headers: {
      "Content-Type": "application/json",
    },
    body: JSON.stringify(payload),
  });
}

export function deleteWorkDiaryEntry(id: number): Promise<void> {
  return request<void>(`/api/workdiary/${id}`, {
    method: "DELETE",
  });
}

export const getWorkDiaryAttachmentDownloadUrl = (
  entryId: number,
  attachmentId: number,
): string => {
  return `${apiBaseUrl}/api/workdiary/${entryId}/attachments/${attachmentId}/download`;
};

// The attachment blob is a PRIVATE blob served by an authenticated endpoint, so it
// cannot be used directly as an <img src> (in production the API is cross-origin).
// Fetch it with credentials and let the caller build an objectURL for previewing.
export async function fetchWorkDiaryAttachmentBlob(
  entryId: number,
  attachmentId: number,
): Promise<Blob> {
  const response = await fetch(
    getWorkDiaryAttachmentDownloadUrl(entryId, attachmentId),
    { credentials: "include" },
  );
  if (!response.ok) {
    const text = await response.text();
    throw new ApiError(response.status, text || response.statusText);
  }
  return response.blob();
}
